import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, getDocs, orderBy, query, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

function Panels() {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPanel, setEditingPanel] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [panelToDelete, setPanelToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sortConfig, setSortConfig] = useState({ key: 'order', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    panelId: '',
    title: '',
    order: '',
    subscriptionLevel: '',
    published: '',
    showErrors: ''
  });
  
  const [formData, setFormData] = useState({
    panelId: '',
    title: '',
    order: 0,
    subscriptionLevel: 0,
    published: false,
    showErrors: false,
    loadingText: '',
    defaultErrorMessage: '',
    modelId: '',
    version: '',
    outputFormat: '',
    voiceId: '',
    entitlements: [],
    responsesLimitExceeded: '',
    defaultResponse: '',
    purchaseText: '',
    offering: ''
  });

  const fetchPanels = async () => {
    setLoading(true);
    try {
      const panelsRef = collection(db, 'ai-panels');
      const q = query(panelsRef, orderBy('order', 'asc'));
      
      const querySnapshot = await getDocs(q);
      const panelsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        panelsData.push({
          id: doc.id,
          ...data
        });
      });

      setPanels(panelsData);
    } catch (error) {
      console.error('Error fetching panels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (panelId) => {
    setExpandedRow(expandedRow === panelId ? null : panelId);
  };

  const handleOpenDialog = (panel = null) => {
    if (panel) {
      setEditingPanel(panel);
      setFormData({
        panelId: panel.panelId || '',
        title: panel.title || '',
        order: panel.order ?? 0,
        subscriptionLevel: panel.subscriptionLevel ?? 0,
        published: panel.published ?? false,
        showErrors: panel.showErrors ?? false,
        loadingText: panel.loadingText || '',
        defaultErrorMessage: panel.defaultErrorMessage || '',
        modelId: panel.modelId || '',
        version: panel.version || '',
        outputFormat: panel.outputFormat || '',
        voiceId: panel.voiceId || '',
        entitlements: panel.entitlements || [],
        responsesLimitExceeded: panel.responsesLimitExceeded || '',
        defaultResponse: panel.defaultResponse || '',
        purchaseText: panel.purchaseText || '',
        offering: panel.offering || ''
      });
    } else {
      setEditingPanel(null);
      setFormData({
        panelId: '',
        title: '',
        order: 0,
        subscriptionLevel: 0,
        published: false,
        showErrors: false,
        loadingText: '',
        defaultErrorMessage: '',
        modelId: '',
        version: '',
        outputFormat: '',
        voiceId: '',
        entitlements: [],
        responsesLimitExceeded: '',
        defaultResponse: '',
        purchaseText: '',
        offering: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPanel(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePanel = async () => {
    try {
      const panelData = {
        panelId: formData.panelId,
        title: formData.title,
        order: parseInt(formData.order) || 0,
        subscriptionLevel: parseInt(formData.subscriptionLevel) || 0,
        published: formData.published,
        showErrors: formData.showErrors,
        loadingText: formData.loadingText,
        defaultErrorMessage: formData.defaultErrorMessage,
        modelId: formData.modelId,
        version: formData.version,
        outputFormat: formData.outputFormat,
        voiceId: formData.voiceId,
        entitlements: formData.entitlements,
        responsesLimitExceeded: formData.responsesLimitExceeded,
        defaultResponse: formData.defaultResponse,
        purchaseText: formData.purchaseText,
        offering: formData.offering
      };

      if (editingPanel) {
        // Update existing panel
        const panelRef = doc(db, 'ai-panels', editingPanel.id);
        await updateDoc(panelRef, panelData);
        setSnackbar({ open: true, message: 'Panel updated successfully', severity: 'success' });
      } else {
        // Create new panel
        await addDoc(collection(db, 'ai-panels'), panelData);
        setSnackbar({ open: true, message: 'Panel created successfully', severity: 'success' });
      }

      handleCloseDialog();
      fetchPanels();
    } catch (error) {
      console.error('Error saving panel:', error);
      setSnackbar({ open: true, message: 'Error saving panel: ' + error.message, severity: 'error' });
    }
  };

  const handleDeleteClick = (panel, event) => {
    event.stopPropagation();
    setPanelToDelete(panel);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!panelToDelete) return;

    try {
      await deleteDoc(doc(db, 'ai-panels', panelToDelete.id));
      setSnackbar({ open: true, message: 'Panel deleted successfully', severity: 'success' });
      setDeleteConfirmOpen(false);
      setPanelToDelete(null);
      fetchPanels();
    } catch (error) {
      console.error('Error deleting panel:', error);
      setSnackbar({ open: true, message: 'Error deleting panel: ' + error.message, severity: 'error' });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPanelToDelete(null);
  };

  const handleRequestSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const sortedPanels = React.useMemo(() => {
    // First filter the panels
    let filtered = panels.filter(panel => {
      const matchesPanelId = filters.panelId === '' || (panel.panelId || '').toLowerCase().includes(filters.panelId.toLowerCase());
      const matchesTitle = filters.title === '' || (panel.title || '').toLowerCase().includes(filters.title.toLowerCase());
      const matchesOrder = filters.order === '' || String(panel.order ?? '').includes(filters.order);
      const matchesSubscriptionLevel = filters.subscriptionLevel === '' || String(panel.subscriptionLevel ?? '').includes(filters.subscriptionLevel);
      
      // For boolean filters, allow filtering by 'true', 'false', or empty
      const matchesPublished = filters.published === '' || 
        (filters.published.toLowerCase() === 'true' && panel.published) ||
        (filters.published.toLowerCase() === 'false' && !panel.published);
      const matchesShowErrors = filters.showErrors === '' || 
        (filters.showErrors.toLowerCase() === 'true' && panel.showErrors) ||
        (filters.showErrors.toLowerCase() === 'false' && !panel.showErrors);
      
      return matchesPanelId && matchesTitle && matchesOrder && matchesSubscriptionLevel && matchesPublished && matchesShowErrors;
    });

    // Then sort the filtered results
    const sorted = [...filtered];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle undefined/null values
        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        // Handle boolean values
        if (typeof aVal === 'boolean') {
          aVal = aVal ? 1 : 0;
          bVal = bVal ? 1 : 0;
        }

        // Handle string comparison
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Handle numeric comparison
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }
    return sorted;
  }, [panels, sortConfig, filters]);

  useEffect(() => {
    fetchPanels();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          AI Panels Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Panel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell width="50">
                  <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                    <IconButton
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                      color={showFilters ? "primary" : "default"}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'panelId'}
                    direction={sortConfig.key === 'panelId' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('panelId')}
                  >
                    Panel ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'title'}
                    direction={sortConfig.key === 'title' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortConfig.key === 'order'}
                    direction={sortConfig.key === 'order' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('order')}
                  >
                    Order
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortConfig.key === 'subscriptionLevel'}
                    direction={sortConfig.key === 'subscriptionLevel' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('subscriptionLevel')}
                  >
                    Subscription Level
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortConfig.key === 'published'}
                    direction={sortConfig.key === 'published' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('published')}
                  >
                    Published
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortConfig.key === 'showErrors'}
                    direction={sortConfig.key === 'showErrors' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('showErrors')}
                  >
                    Show Errors
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" width="120">Actions</TableCell>
              </TableRow>
              {showFilters && (
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter panel ID..."
                      value={filters.panelId}
                      onChange={(e) => handleFilterChange('panelId', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter title..."
                      value={filters.title}
                      onChange={(e) => handleFilterChange('title', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter order..."
                      value={filters.order}
                      onChange={(e) => handleFilterChange('order', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter level..."
                      value={filters.subscriptionLevel}
                      onChange={(e) => handleFilterChange('subscriptionLevel', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="true/false"
                      value={filters.published}
                      onChange={(e) => handleFilterChange('published', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="true/false"
                      value={filters.showErrors}
                      onChange={(e) => handleFilterChange('showErrors', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      variant="outlined"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {sortedPanels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 4 }}>
                      No panels found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedPanels.map((panel) => (
                  <React.Fragment key={panel.id}>
                    <TableRow 
                      onClick={() => handleRowClick(panel.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        ...(expandedRow === panel.id && { backgroundColor: 'rgba(25, 118, 210, 0.08)' })
                      }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(panel.id);
                          }}
                        >
                          {expandedRow === panel.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={panel.panelId || panel.id} 
                          variant="outlined" 
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>
                        {panel.title || 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={panel.order ?? 'N/A'} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={panel.subscriptionLevel ?? 'N/A'} 
                          size="small"
                          color={panel.subscriptionLevel === 0 ? 'default' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={panel.published ? 'Yes' : 'No'} 
                          size="small"
                          color={panel.published ? 'success' : 'default'}
                          variant={panel.published ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={panel.showErrors ? 'Yes' : 'No'} 
                          size="small"
                          color={panel.showErrors ? 'warning' : 'default'}
                          variant={panel.showErrors ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(panel);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeleteClick(panel, e)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={expandedRow === panel.id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Panel Details
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Document ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel._id || panel.id}
                                  </Typography>
                                </Box>
                                
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Panel ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel.panelId || 'N/A'}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Order:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel.order ?? 'N/A'}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Subscription Level:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel.subscriptionLevel ?? 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Title:
                                </Typography>
                                <Typography variant="body2">
                                  {panel.title || 'N/A'}
                                </Typography>
                              </Box>

                              {panel.loadingText && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Loading Text:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel.loadingText}
                                  </Typography>
                                </Box>
                              )}

                              {panel.defaultErrorMessage && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Default Error Message:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2">
                                      {panel.defaultErrorMessage}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {panel.modelId && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Model ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel.modelId}
                                  </Typography>
                                </Box>
                              )}

                              {panel.voiceId && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Voice ID:
                                  </Typography>
                                  <Typography variant="body2">
                                    {panel.voiceId}
                                  </Typography>
                                </Box>
                              )}

                              {panel.defaultResponse && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Default Response:
                                  </Typography>
                                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2">
                                      {panel.defaultResponse}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {panel.entitlements && panel.entitlements.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Entitlements:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {panel.entitlements.map((entitlement, index) => (
                                      <Chip key={index} label={entitlement} size="small" />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                                <FormControlLabel
                                  control={
                                    <Switch 
                                      checked={panel.published ?? false} 
                                      disabled 
                                    />
                                  }
                                  label="Published"
                                />
                                <FormControlLabel
                                  control={
                                    <Switch 
                                      checked={panel.showErrors ?? false} 
                                      disabled 
                                    />
                                  }
                                  label="Show Errors"
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Panel Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPanel ? 'Edit Panel' : 'Add New Panel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Panel ID"
              value={formData.panelId}
              onChange={(e) => handleFormChange('panelId', e.target.value)}
              fullWidth
              required
              helperText="Unique identifier for the panel"
            />
            
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              fullWidth
              required
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Order"
                type="number"
                value={formData.order}
                onChange={(e) => handleFormChange('order', e.target.value)}
                fullWidth
                required
                helperText="Display order"
              />
              
              <TextField
                label="Subscription Level"
                type="number"
                value={formData.subscriptionLevel}
                onChange={(e) => handleFormChange('subscriptionLevel', e.target.value)}
                fullWidth
                required
                helperText="0 = Free, 1+ = Premium"
              />
            </Box>

            <TextField
              label="Loading Text"
              value={formData.loadingText}
              onChange={(e) => handleFormChange('loadingText', e.target.value)}
              fullWidth
              helperText="Text shown while panel is loading"
            />

            <TextField
              label="Default Error Message"
              value={formData.defaultErrorMessage}
              onChange={(e) => handleFormChange('defaultErrorMessage', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Error message shown when panel fails to load"
            />

            <TextField
              label="Model ID"
              value={formData.modelId}
              onChange={(e) => handleFormChange('modelId', e.target.value)}
              fullWidth
              helperText="AI model identifier (e.g., eleven_flash_v2_5)"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Version"
                value={formData.version}
                onChange={(e) => handleFormChange('version', e.target.value)}
                fullWidth
                helperText="Model version"
              />

              <TextField
                label="Output Format"
                value={formData.outputFormat}
                onChange={(e) => handleFormChange('outputFormat', e.target.value)}
                fullWidth
                helperText="Output format"
              />
            </Box>

            <TextField
              label="Voice ID"
              value={formData.voiceId}
              onChange={(e) => handleFormChange('voiceId', e.target.value)}
              fullWidth
              helperText="Voice identifier for audio output"
            />

            <TextField
              label="Default Response"
              value={formData.defaultResponse}
              onChange={(e) => handleFormChange('defaultResponse', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Default AI response template (use {{variable}} for placeholders)"
            />

            <TextField
              label="Responses Limit Exceeded Message"
              value={formData.responsesLimitExceeded}
              onChange={(e) => handleFormChange('responsesLimitExceeded', e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Message when user exceeds daily response limit"
            />

            <TextField
              label="Entitlements (comma-separated)"
              value={formData.entitlements.join(', ')}
              onChange={(e) => handleFormChange('entitlements', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              fullWidth
              helperText="Access levels required (e.g., ai-level-1, ai-level-2)"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Purchase Text"
                value={formData.purchaseText}
                onChange={(e) => handleFormChange('purchaseText', e.target.value)}
                fullWidth
                helperText="CTA text for subscription"
              />

              <TextField
                label="Offering"
                value={formData.offering}
                onChange={(e) => handleFormChange('offering', e.target.value)}
                fullWidth
                helperText="Offering identifier"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={(e) => handleFormChange('published', e.target.checked)}
                  />
                }
                label="Published"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showErrors}
                    onChange={(e) => handleFormChange('showErrors', e.target.checked)}
                  />
                }
                label="Show Errors"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSavePanel} 
            variant="contained"
            disabled={!formData.panelId || !formData.title}
          >
            {editingPanel ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the panel "{panelToDelete?.title}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Panels;
